/* FizzyIQ — mission journey reveal + spine draw (fizzy-msn-journey).
   Reveal: the one ruled rise+fade per section instance, IO-gated with the
   unconditional fallback (project gotcha: stuck-IO environments).
   Spine draw, desktop: scroll-linked coral fill (scaleY) down the center
   track; dots ignite (.is-past) as it passes.
   Spine draw, mobile (≤820): ONE SVG path — out of the badge's left side,
   around the corner, down the rail — drawn as dashed track + coral stroke
   scrubbed by the same scroll progress (stroke-dashoffset). Dots ignite off
   the drawn tip's real position (getPointAtLength).
   The arrival: the close entering view completes the line and blooms the
   ground behind the pouches. Reversible — scrolling back up retracts it.
   prefers-reduced-motion: geometry still builds; everything renders in the
   completed state with no listeners. */
(function () {
  'use strict';

  var EASE_RAIL = window.matchMedia ? window.matchMedia('(max-width: 820px)') : null;
  var PRM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initSpineDraw() {
    var flows = document.querySelectorAll('[data-msnj-spine]');
    if (!flows.length) return;

    flows.forEach(function (flow) {
      var fill = flow.querySelector('[data-msnj-fill]');
      var chaps = Array.prototype.slice.call(flow.querySelectorAll('[data-msnj-chap]'));
      var svg = flow.querySelector('[data-msnj-path]');
      var track = svg ? svg.querySelector('.msnj-spine__path-track') : null;
      var draw = svg ? svg.querySelector('.msnj-spine__path-draw') : null;
      var sec = flow.closest('.msnj');
      var badge = sec ? sec.querySelector('.msnj-spine__badge') : null;
      var closeEl = sec ? sec.querySelector('.msnj-close') : null;
      var pathLen = 0;

      function isRail() { return EASE_RAIL && EASE_RAIL.matches; }

      /* Build the mobile path: M (badge left edge, badge mid-Y) → H to the
         corner → quarter-arc → V to the rail bottom. Coordinates are in the
         SVG's own box, which sits at top: -46px relative to the flow. */
      function buildPath() {
        if (!svg || !track || !draw || !badge) return;
        if (!isRail()) { svg.style.display = 'none'; return; }
        svg.style.display = 'block';
        var fr = flow.getBoundingClientRect();
        var br = badge.getBoundingClientRect();
        var svgTop = fr.top - 46;
        var startX = br.left - fr.left;            /* badge's left edge */
        var startY = (br.top + br.bottom) / 2 - svgTop;
        var railX = 8;
        var r = 14;
        var bottomY = fr.height + 46 - 8;
        var d = 'M ' + startX.toFixed(1) + ' ' + startY.toFixed(1) +
                ' L ' + (railX + r) + ' ' + startY.toFixed(1) +
                ' A ' + r + ' ' + r + ' 0 0 0 ' + railX + ' ' + (startY + r).toFixed(1) +
                ' L ' + railX + ' ' + bottomY.toFixed(1);
        svg.setAttribute('viewBox', '0 0 ' + Math.max(fr.width, startX + 1).toFixed(0) + ' ' + (bottomY + 1).toFixed(0));
        svg.setAttribute('width', Math.max(fr.width, startX + 1).toFixed(0));
        svg.setAttribute('height', (bottomY + 1).toFixed(0));
        track.setAttribute('d', d);
        draw.setAttribute('d', d);
        pathLen = draw.getTotalLength();
        draw.style.strokeDasharray = pathLen + ' ' + pathLen;
        draw.style.strokeDashoffset = String(pathLen);
      }

      function render(progress) {
        if (isRail() && svg && pathLen) {
          draw.style.strokeDashoffset = String(pathLen * (1 - progress));
          /* Dots ignite when the drawn tip passes them. */
          var tip = draw.getPointAtLength(pathLen * progress);
          var fr = flow.getBoundingClientRect();
          var tipDocY = fr.top - 46 + tip.y;
          chaps.forEach(function (c) {
            var cr = c.getBoundingClientRect();
            c.classList.toggle('is-past', cr.top + 40 <= tipDocY + 1);
          });
        } else if (fill) {
          fill.style.transform = 'scaleY(' + progress.toFixed(4) + ')';
          var r2 = flow.getBoundingClientRect();
          var fillY = r2.top + r2.height * progress;
          chaps.forEach(function (c) {
            var cr = c.getBoundingClientRect();
            c.classList.toggle('is-past', (cr.top + cr.bottom) / 2 <= fillY + 1);
          });
        }
      }

      function progressNow() {
        var r = flow.getBoundingClientRect();
        var anchor = window.innerHeight * 0.55;
        var progress = (anchor - r.top) / r.height;
        progress = Math.max(0, Math.min(1, progress));
        /* The arrival: the close entering view completes the story, because
           the 0.55 anchor alone can never reach 1 when little content follows
           the flow.

           Two-way as of 2026-08-27 (Sam: "as I scroll back up the horizon
           line should go back up"). It used to latch on and pin progress to
           1 forever, so the line stayed fully drawn once you had reached the
           bottom. The trigger now releases with a hysteresis band — it arms
           at 0.8 of the viewport and disarms at 1.05 — so the state cannot
           flicker on and off around a single boundary while scrubbing. */
        var closeTop = closeEl ? closeEl.getBoundingClientRect().top : Infinity;
        var arrived = sec && sec.classList.contains('is-arrived');
        if (sec) {
          if (!arrived && (progress >= 0.98 || closeTop < window.innerHeight * 0.8)) {
            sec.classList.add('is-arrived');
            arrived = true;
          } else if (arrived && progress < 0.98 && closeTop > window.innerHeight * 1.05) {
            sec.classList.remove('is-arrived');
            arrived = false;
          }
        }
        if (arrived) progress = 1;
        return progress;
      }

      buildPath();

      if (PRM) {
        /* Completed state, no listeners. */
        if (sec) sec.classList.add('is-arrived');
        render(1);
        return;
      }

      var ticking = false;
      function sync() { ticking = false; render(progressNow()); }
      function onScroll() {
        if (!ticking) { ticking = true; requestAnimationFrame(sync); }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', function () {
        buildPath();
        onScroll();
      }, { passive: true });
      sync();
    });
  }

  function init() {
    initSpineDraw();
    var sections = document.querySelectorAll('.msnj');
    if (!sections.length) return;

    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (s) { s.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px' });

    sections.forEach(function (s) {
      io.observe(s);
      setTimeout(function () {
        var r = s.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) s.classList.add('is-in');
      }, 900);
    });
  }

  /* ── RAIL layout: the fill scrubs, the dots ignite ────────────────────
     Self-contained on purpose. The spine's draw above measures an SVG path and
     is intricate; this shares none of it, so the two layouts cannot break each
     other and only one is ever in the DOM.

     Geometry is measured from the DOTS, not from the items: the rail must run
     from the first dot's centre to the last dot's centre, or it overshoots the
     copy at both ends and reads as a stray line.

     rAF-throttled scroll rather than an observer per item — this is the
     convention the rest of the theme already uses for scroll-linked fills, and
     IO callbacks get stuck here after fast scrolls. */
  function initRail() {
    var rails = document.querySelectorAll('[data-msnj-rail]');
    if (!rails.length) return;
    /* The held-back state belongs to the script that releases it: without
       this class the CSS keeps every chapter at 0.3 if the asset never runs. */
    Array.prototype.forEach.call(rails, function (r) { r.classList.add('is-scrubbing'); });

    Array.prototype.forEach.call(rails, function (rail) {
      var items = Array.prototype.slice.call(rail.querySelectorAll('[data-msnj-stage]'));
      if (!items.length) return;

      function dotOf(item) { return item.querySelector('.msnj-rail__dot'); }

      var sec = rail.closest('.msnj');
      var closeEl = sec ? sec.querySelector('.msnj-close') : null;

      function measure() {
        var first = dotOf(items[0]);
        var last = dotOf(items[items.length - 1]);
        if (!first || !last) return;
        var railRect = rail.getBoundingClientRect();
        var f = first.getBoundingClientRect();
        var l = last.getBoundingClientRect();
        var topY = (f.top + f.height / 2) - railRect.top;
        var botY = (l.top + l.height / 2) - railRect.top;
        rail.style.setProperty('--rail-top', topY + 'px');
        rail.style.setProperty('--rail-bottom', (railRect.height - botY) + 'px');
      }

      function apply() {
        ticking = false;
        if (PRM) return;   /* reduced motion: CSS already renders it complete */

        var railRect = rail.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        /* The trigger sits a little above the vertical middle, so a chapter
           lights as it settles into reading position rather than at the very
           bottom edge of the screen. */
        var trigY = vh * 0.62;

        /* CONTINUOUS: the fill is the trigger line's own position along the
           rail, clamped to the span between the first and last dot. Anchoring
           it to the last PASSED dot instead makes the line sit still between
           chapters and then lurch. */
        var cs = getComputedStyle(rail);
        var top = parseFloat(cs.getPropertyValue('--rail-top')) || 0;
        var bottom = parseFloat(cs.getPropertyValue('--rail-bottom')) || 0;
        var span = Math.max(0, railRect.height - top - bottom);
        var raw = trigY - railRect.top - top;
        rail.style.setProperty('--rail-fill', Math.min(Math.max(raw, 0), span) + 'px');

        /* The CHAPTER decides, not its tick. Since the album redesign the
           year sits under the photograph on a spread, so a dot-driven test
           held a plate, a title and half a paragraph at 0.3 while they were
           fully on screen. The thread endpoints still come from the dots. */
        items.forEach(function (item) {
          item.classList.toggle('is-active', item.getBoundingClientRect().top <= trigY - 80);
        });

        /* The close (fan + CTA) deals when it enters view. Same hysteresis
           band as the spine's arrival so it can't flicker at the boundary. */
        if (sec && closeEl) {
          var closeTop = closeEl.getBoundingClientRect().top;
          var arrived = sec.classList.contains('is-arrived');
          if (!arrived && closeTop < vh * 0.8) sec.classList.add('is-arrived');
          else if (arrived && closeTop > vh * 1.05) sec.classList.remove('is-arrived');
        }
      }

      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(apply);
      }

      function remeasure() { measure(); apply(); }

      /* Reduced motion: apply() bails early, so land the completed state here. */
      if (PRM && sec) sec.classList.add('is-arrived');

      measure();
      apply();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', remeasure);
      /* Images land after first paint and move every dot below them. */
      window.addEventListener('load', remeasure);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); initRail(); });
  } else {
    init();
    initRail();
  }
})();
